import * as echarts from "echarts";
import React from "react";

import styles from "./charts.module.css";
import { chartAxisNumber, chartNumber, displayText } from "../utils/format";
import { filterChartSeries, pointDate } from "../utils/chartRange";

export const ATTRIBUTION_COLORS = {
  price: "#007a3d",
  dividend: "#2563eb",
  realized: "#f97316",
  fee: "#ef4444",
  other: "#8b5cf6",
  total: "#006b3c",
  positive: "#007a3d",
  negative: "#ef4444",
};

function attributionColor(item) {
  if (item?.color) return item.color;
  if (item?.total) return ATTRIBUTION_COLORS.total;
  return Number(item?.value ?? 0) < 0 ? ATTRIBUTION_COLORS.negative : ATTRIBUTION_COLORS.positive;
}

export function LineChart({
  nav = 1.2864,
  navSeries = null,
  benchmarkSeries = null,
  chartRange = "all",
  useDemoFallback = true,
  percentMode = false,
  seriesName = "涨跌幅",
  showLegend = true,
  showActiveMemberCount = false,
  additionalSeries = [],
  rebaseToStart = true,
}) {
  const option = React.useMemo(() => {
    const targetNav = Number(nav) || 1;
    const rangeNavSeries = filterChartSeries(navSeries, chartRange);
    const filteredBenchmarkSeries = filterChartSeries(benchmarkSeries, chartRange);
    const benchmarkByDate = new Map(
      filteredBenchmarkSeries.map((point) => [
        pointDate(point),
        Number(point.value ?? point.growth_multiple ?? point.nav),
      ]),
    );
    const hasBenchmark = [...benchmarkByDate.values()].some((value) => Number.isFinite(value) && value > 0);
    const commonSeries = rangeNavSeries.filter((point) => {
      const navValue = Number(point.unit_nav ?? point.nav ?? point.value ?? point.growth_multiple ?? point);
      const benchmarkValue = benchmarkByDate.get(pointDate(point));
      return Number.isFinite(navValue) && navValue > 0 && Number.isFinite(benchmarkValue) && benchmarkValue > 0;
    });
    const filteredNavSeries = hasBenchmark && commonSeries.length >= 2 ? commonSeries : rangeNavSeries;
    const useComparableBenchmark = hasBenchmark && commonSeries.length >= 2;
    const hasSeries = filteredNavSeries.length > 0;
    const portfolioSeries = hasSeries
      ? filteredNavSeries.map((point) => Number(point.unit_nav ?? point.nav ?? point.value ?? point.growth_multiple ?? point))
      : useDemoFallback
        ? [1.08, 1.24, 1.46, 1.55, 1.7, 1.77, 1.9, 2.05, Math.max(targetNav, 1.2)]
        : [];
    const categories = hasSeries
      ? filteredNavSeries.map(pointDate)
      : useDemoFallback
        ? ["2018-01", "2019-01", "2020-01", "2021-01", "2022-01", "2023-01", "2024-01", "2025-01", "2026-06"]
        : [];
    const hs300Series = hasSeries
      ? categories.map((date) => useComparableBenchmark ? benchmarkByDate.get(date) : null)
      : useDemoFallback
        ? [1.02, 1.16, 1.31, 1.22, 1.38, 1.35, 1.48, 1.58, 1.63]
        : [];
    // Always rebase to start of selected range so chart starts at 1 (or 0% in percentMode)
    const baseNav = hasSeries && portfolioSeries.length > 0 && portfolioSeries[0] > 0
      ? portfolioSeries[0] : null;
    const displayPortfolio = baseNav !== null && rebaseToStart
      ? portfolioSeries.map((v) => percentMode ? v / baseNav - 1 : v / baseNav)
      : portfolioSeries;
    const firstBenchNav = baseNav !== null && rebaseToStart
      ? (hs300Series.find((v) => v !== null && Number.isFinite(v) && v > 0) ?? null) : null;
    const displayHs300 = firstBenchNav && firstBenchNav > 0
      ? hs300Series.map((v) => (v !== null && Number.isFinite(v)) ? (percentMode ? v / firstBenchNav - 1 : v / firstBenchNav) : null)
      : hs300Series;
    const displayAdditional = additionalSeries.map((item) => {
      const byDate = new Map((item.points ?? []).map((point) => [
        pointDate(point),
        Number(point.value ?? point.growth_multiple ?? point.normalized ?? point.raw),
      ]));
      const values = categories.map((date) => byDate.get(date) ?? null);
      const base = values.find((value) => Number.isFinite(value) && value !== 0);
      return {
        ...item,
        values: base
          ? values.map((value) => Number.isFinite(value)
            ? (percentMode ? value / base - 1 : value / base)
            : null)
          : values.map(() => null),
      };
    });
    const chartValues = [
      ...displayPortfolio,
      ...displayHs300,
      ...displayAdditional.flatMap((item) => item.values),
    ].filter(Number.isFinite);
    const minValue = percentMode ? nicePercentAxisMin(chartValues) : niceAxisMin(chartValues);
    const maxValue = percentMode ? nicePercentAxisMax(chartValues) : niceAxisMax(chartValues);
    const series = [
      {
        name: seriesName,
        type: "line",
        smooth: true,
        data: displayPortfolio,
        lineStyle: { color: "#007a3d", width: 3 },
        itemStyle: { color: "#007a3d" },
        areaStyle: percentMode ? {
          color: {
            type: "linear",
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: "rgba(0, 122, 61, 0.16)" },
              { offset: 1, color: "rgba(0, 122, 61, 0.00)" },
            ],
          },
        } : undefined,
        emphasis: { focus: "series" },
        showSymbol: false,
      },
    ];
    if (displayHs300.some((value) => value !== null && Number.isFinite(value))) {
      series.push({
        name: "沪深300",
        type: "line",
        smooth: true,
        data: displayHs300,
        lineStyle: { color: "#356dff", width: 2, type: [4, 3] },
        itemStyle: { color: "#356dff" },
        emphasis: { focus: "series" },
        showSymbol: false,
      });
    }
    displayAdditional.forEach((item, index) => {
      if (!item.values.some((value) => value !== null && Number.isFinite(value))) return;
      series.push({
        name: item.name,
        type: "line",
        smooth: true,
        data: item.values,
        lineStyle: {
          color: item.color ?? ["#8b5cf6", "#d97706"][index % 2],
          width: 2,
          type: item.dashed ? [5, 4] : "solid",
        },
        itemStyle: { color: item.color ?? ["#8b5cf6", "#d97706"][index % 2] },
        emphasis: { focus: "series" },
        showSymbol: false,
      });
    });
    return {
        legend: {
          show: showLegend,
          top: 0,
          left: 0,
          itemWidth: 18,
          itemHeight: 10,
          textStyle: { color: "#53606f" },
        },
        grid: { left: 52, right: 24, top: showLegend ? 42 : 18, bottom: 36 },
        tooltip: percentMode
          ? {
              trigger: "axis",
              valueFormatter: chartPercent,
              formatter: showActiveMemberCount ? (params) => formatPercentTooltip(params, filteredNavSeries) : undefined,
            }
          : { trigger: "axis", valueFormatter: chartNumber },
        xAxis: {
          type: "category",
          data: categories,
          axisLabel: {
            formatter: (value) => String(value).slice(0, 7),
            hideOverlap: true,
          },
        },
        yAxis: {
          type: "value",
          min: minValue,
          max: maxValue,
          scale: true,
          splitNumber: 5,
          axisLabel: percentMode
            ? { formatter: (value) => chartPercent(value) }
            : { formatter: (value) => chartAxisNumber(value) },
        },
        series,
      };
  }, [nav, navSeries, benchmarkSeries, chartRange, useDemoFallback, percentMode, showLegend, showActiveMemberCount, seriesName, additionalSeries, rebaseToStart]);
  if (!useDemoFallback && (!Array.isArray(navSeries) || navSeries.length === 0)) {
    return (
      <div className="chart-empty">
        暂无净值序列数据。请点击"刷新数据"重新计算快照。
      </div>
    );
  }
  return <Chart option={option} />;
}

export function TradeKlineChart({
  priceSeries = [], transactions = [], performanceSeries = [], chartRange = "all",
  costLineMode = "dividend_adjusted", assetEvents = [], isOpenFund = false,
  comparisonSeries = [], onOpenTradeLedger, onOpenEventTimeline,
}) {
  const [selectedTradeDay, setSelectedTradeDay] = React.useState(null);
  const handleChartClick = React.useCallback((params) => {
    const dayTrades = params?.data?.dayTrades ?? [];
    const companyEvents = params?.data?.companyEvents ?? [];
    if ((!Array.isArray(dayTrades) || dayTrades.length === 0) && (!Array.isArray(companyEvents) || companyEvents.length === 0)) {
      setSelectedTradeDay(null);
      return;
    }
    setSelectedTradeDay({
      tradeDate: params?.data?.coord?.[0] ?? dayTrades[0]?.trade_date,
      transactions: dayTrades,
      companyEvents,
      anchor: {
        x: Number(params?.event?.offsetX ?? 0),
        y: Number(params?.event?.offsetY ?? 0),
      },
    });
  }, []);
  const option = React.useMemo(() => {
    const prices = filterChartSeries(priceSeries, chartRange).filter((point) => (
      [point.open, point.high, point.low, point.close].every((value) => (
        value !== null && value !== "" && value !== undefined && Number.isFinite(Number(value))
      ))
    ));
    const navPrices = filterChartSeries(priceSeries, chartRange).filter((point) => Number.isFinite(Number(point.close)));
    // 场外基金只有日终单位净值，即使上游未来补了衍生 OHLC 字段，也绝不能伪装成
    // 日内价格形成的 K 线；统一使用 NAV 折线和申购/赎回确认点。
    const useNavLine = isOpenFund ? navPrices.length > 0 : !prices.length && navPrices.length > 0;
    if (!prices.length && !useNavLine) return null;
    const visiblePrices = useNavLine ? navPrices : prices;
    const dates = visiblePrices.map((point) => point.date ?? point.trade_date ?? "");
    const visibleDates = new Set(dates);
    const costField = costLineMode === "transaction"
      ? "cost_per_share"
      : "dividend_adjusted_cost_per_share";
    const costLineName = costLineMode === "transaction"
      ? "交易成本价（不扣分红）"
      : "成本价";
    const costByDate = new Map((performanceSeries ?? []).map((point) => [
      point.date ?? point.trade_date,
      point[costField] == null ? null : Number(point[costField]),
    ]));
    const tradesByDate = new Map();
    for (const trade of transactions ?? []) {
      if (!visibleDates.has(trade.trade_date) || !["BUY", "SELL", "SUBSCRIBE", "REDEEM"].includes(trade.action)) continue;
      const dayTrades = tradesByDate.get(trade.trade_date) ?? [];
      dayTrades.push(trade);
      tradesByDate.set(trade.trade_date, dayTrades);
    }
    // 同日交易已先聚合为一个标记。相邻交易日若出现同类标记（例如连续两天申购），
    // 则交替放到两条垂直轨道，避免数据密集区的圆点完全重叠。
    let previousMarker = null;
    const markers = [...tradesByDate.entries()].map(([tradeDate, dayTrades]) => {
      const hasBuy = dayTrades.some((trade) => ["BUY", "SUBSCRIBE"].includes(trade.action));
      const hasSell = dayTrades.some((trade) => ["SELL", "REDEEM"].includes(trade.action));
      const label = isOpenFund
        ? hasBuy && hasSell ? "申/赎" : hasBuy ? "申" : "赎"
        : hasBuy && hasSell ? "B/S" : hasBuy ? "B" : "S";
      const representativeTrade = dayTrades[dayTrades.length - 1];
      const pricePoint = visiblePrices.find((point) => (point.date ?? point.trade_date) === tradeDate);
      // 股票标记锚定到当日实体外的高/低点，再向外偏移，避免 B/S 覆盖蜡烛图。
      const markerPrice = !useNavLine && pricePoint
        ? Number(hasBuy ? pricePoint.high : pricePoint.low)
        : Number(representativeTrade.price);
      const dateIndex = dates.indexOf(tradeDate);
      const repeatsNearby = previousMarker
        && previousMarker.label === label
        && dateIndex - previousMarker.dateIndex <= 2;
      const markerLane = repeatsNearby ? (previousMarker.lane + 1) % 2 : 0;
      previousMarker = { label, dateIndex, lane: markerLane };
      const baseVerticalOffset = !useNavLine
        ? (hasBuy ? -14 : 14)
        : -12;
      const laneOffset = markerLane * 18;
      return {
        coord: [tradeDate, markerPrice],
        value: label,
        symbol: "circle",
        symbolSize: label === "B/S" ? 28 : 20,
        symbolOffset: [0, baseVerticalOffset < 0 ? baseVerticalOffset - laneOffset : baseVerticalOffset + laneOffset],
        itemStyle: { color: label === "B" ? "#16834a" : label === "S" ? "#e85b55" : "#7c3aed" },
        label: { show: true, formatter: label, color: "#fff", fontWeight: 800, fontSize: label === "B/S" ? 10 : 12 },
        dayTrades,
      };
    });
    const companyEventsByDate = new Map();
    for (const event of assetEvents ?? []) {
      // 账本 v2 将分红拆为权益确认与到账；成本价在权益确认日变化，图上只在
      // 该日显示一个“分”标记，到账记录仍完整保留在事件时间线。
      if (!["cash_dividend", "cash_dividend_entitlement", "share_bonus", "rights_issue", "rights_issue_confirmation"].includes(event.event_type)) continue;
      const displayDate = dates.find((day) => day >= event.date);
      if (!displayDate) continue;
      const dayEvents = companyEventsByDate.get(displayDate) ?? [];
      dayEvents.push(event);
      companyEventsByDate.set(displayDate, dayEvents);
    }
    const companyEventMarkers = [...companyEventsByDate.entries()].map(([displayDate, companyEvents]) => {
      const pricePoint = visiblePrices.find((point) => (point.date ?? point.trade_date) === displayDate);
      const label = companyEventMarkerLabel(companyEvents);
      return {
        coord: [displayDate, Number(pricePoint?.high ?? pricePoint?.close)],
        value: label,
        symbol: "roundRect",
        symbolSize: [Math.max(22, label.length * 12 + 8), 18],
        symbolOffset: [0, -17],
        itemStyle: { color: companyEventMarkerColor(companyEvents) },
        label: { show: true, formatter: label, color: "#fff", fontWeight: 800, fontSize: 10 },
        companyEvents,
      };
    });
    const candleData = visiblePrices.map((point) => [
      Number(point.open), Number(point.close), Number(point.low), Number(point.high),
    ]);
    const normalizedComparisonSeries = comparisonSeries.map((item, index) => {
      const byDate = new Map((item.points ?? []).map((point) => [
        pointDate(point),
        Number(point.value ?? point.normalized ?? point.raw),
      ]));
      const values = dates.map((day) => byDate.get(day) ?? null);
      const base = values.find((value) => Number.isFinite(value) && value !== 0);
      return {
        ...item,
        color: item.color ?? ["#8b5cf6", "#d97706"][index % 2],
        values: base
          ? values.map((value) => Number.isFinite(value) ? value / base - 1 : null)
          : values.map(() => null),
      };
    });
    return {
      // 顶部工具栏在 ECharts 容器之外；仍需为高点外侧的 B/S、申赎和公司事件
      // 标记预留画布内安全区，避免视觉上钻入图例区域或被裁切。
      grid: { left: 54, right: normalizedComparisonSeries.length ? 58 : 24, top: 42, bottom: 58 },
      // 图例由价格图卡片的工具栏承载，和区间筛选占用同一横向空间，避免 ECharts 默认堆在左上角。
      legend: { show: false },
      tooltip: {
        show: true,
        trigger: "axis",
        axisPointer: { type: "cross" },
        padding: [9, 11],
        borderColor: "#dfe8e3",
        backgroundColor: "rgba(255, 255, 255, 0.97)",
        textStyle: { color: "#344054", fontSize: 12 },
        formatter: (params) => formatKlineTooltip(
          params,
          visiblePrices,
          useNavLine,
          costLineName,
          new Set(normalizedComparisonSeries.map((item) => item.name)),
        ),
      },
      xAxis: { type: "category", data: dates, axisLabel: { formatter: (value) => String(value).slice(0, 7), hideOverlap: true } },
      yAxis: [
        { type: "value", scale: true, axisLabel: { formatter: chartAxisNumber }, splitLine: { lineStyle: { color: "#edf1f4" } } },
        {
          type: "value",
          scale: true,
          position: "right",
          axisLabel: { formatter: chartPercent },
          splitLine: { show: false },
        },
      ],
      series: [
        ...(useNavLine ? [{
          name: "NAV",
          type: "line",
          data: visiblePrices.map((point) => Number(point.close)),
          showSymbol: false,
          lineStyle: { color: "#16834a", width: 2.5 },
          itemStyle: { color: "#16834a" },
          markPoint: { silent: false, data: [...markers, ...companyEventMarkers] },
        }] : [{
          name: "日K（不复权）",
          type: "candlestick",
          data: candleData,
          itemStyle: { color: "#16834a", color0: "#e85b55", borderColor: "#16834a", borderColor0: "#e85b55" },
          markPoint: { silent: false, data: [...markers, ...companyEventMarkers] },
        }]),
        {
          name: costLineName,
          type: "line",
          data: dates.map((day) => costByDate.get(day) ?? null),
          showSymbol: false,
          connectNulls: false,
          lineStyle: { color: "#3566dd", width: 2 },
          itemStyle: { color: "#3566dd" },
        },
        ...normalizedComparisonSeries.map((item) => ({
          name: item.name,
          type: "line",
          yAxisIndex: 1,
          data: item.values,
          showSymbol: false,
          connectNulls: false,
          lineStyle: { color: item.color, width: 2, type: item.dashed ? [5, 4] : "solid" },
          itemStyle: { color: item.color },
        })),
      ],
    };
  }, [priceSeries, transactions, performanceSeries, chartRange, costLineMode, assetEvents, isOpenFund, comparisonSeries]);
  if (!option) return <div className="chart-empty">{isOpenFund ? "暂无基金净值序列，不能展示申购/赎回点。" : "暂无完整 OHLC 日线，不能展示买卖点 K 线。"}</div>;
  const anchorX = selectedTradeDay ? Math.max(12, Math.min(selectedTradeDay.anchor.x + 12, 620)) : 0;
  const anchorY = selectedTradeDay ? Math.max(12, Math.min(selectedTradeDay.anchor.y - 150, 118)) : 0;
  return (
    <div className={styles.tradeKlineChart}>
      <Chart option={option} onChartClick={handleChartClick} />
      {selectedTradeDay && (
        <aside
          className={styles.tradeKlinePopover}
          style={{ left: anchorX, top: anchorY }}
          aria-label={`${selectedTradeDay.tradeDate} 交易明细`}
        >
          <div className={styles.tradeKlinePopoverHead}>
            <div>
              <strong>{selectedTradeDay.tradeDate} 明细</strong>
              <span>当日 {selectedTradeDay.transactions.length} 笔账本交易{selectedTradeDay.companyEvents.length ? ` / ${selectedTradeDay.companyEvents.length} 个公司事件` : ""}</span>
            </div>
            <button type="button" className={styles.tradeKlinePopoverClose} onClick={() => setSelectedTradeDay(null)} aria-label="关闭交易明细">×</button>
          </div>
          <div className={styles.tradeKlinePopoverList}>
            {selectedTradeDay.transactions.map((trade) => {
              const isBuy = ["BUY", "SUBSCRIBE"].includes(trade.action);
              return (
                <article key={trade.trade_id} className={`${styles.tradeKlinePopoverTrade} ${isBuy ? styles.buy : styles.sell}`}>
                  <strong>{isOpenFund ? (isBuy ? "申购" : "赎回") : (isBuy ? "B 买入" : "S 卖出")}</strong>
                  <span>数量</span><span>{formatKlineQuantity(trade.quantity)} {isOpenFund ? "份" : "股"}</span>
                  <span>{isOpenFund ? "确认净值" : "成交价"}</span><span>{formatKlineMoney(trade.price)} 元/{isOpenFund ? "份" : "股"}</span>
                  <span>手续费</span><span>{formatKlineMoney(trade.fee)} 元</span>
                </article>
              );
            })}
            {selectedTradeDay.companyEvents.map((event) => (
              <article key={`${event.event_type}-${event.date}-${event.evidence_ref}`} className={styles.tradeKlinePopoverEvent}>
                <strong>{companyEventLabel(event.event_type)}</strong>
                <span>{event.title}</span>
                <small>{eventImpactLabel(event)}</small>
              </article>
            ))}
          </div>
          {selectedTradeDay.transactions.length > 0 && <button type="button" className={styles.tradeKlinePopoverLink} onClick={onOpenTradeLedger}>查看交易账本 ›</button>}
          {selectedTradeDay.companyEvents.length > 0 && <button type="button" className={styles.tradeKlinePopoverLink} onClick={onOpenEventTimeline}>查看事件时间线 ›</button>}
        </aside>
      )}
    </div>
  );
}

export function DrawdownChart({ navSeries = [], chartRange = "all" }) {
  const option = React.useMemo(() => {
    const filtered = filterChartSeries(navSeries, chartRange);
    if (filtered.length < 2) return null;
    const navValues = filtered.map((p) => Number(p.unit_nav ?? p.nav ?? 1));
    const dates = filtered.map((p) => p.date ?? p.trade_date ?? "");
    let peak = navValues[0];
    const drawdownPct = navValues.map((v) => {
      peak = Math.max(peak, v);
      return peak > 0 ? ((v - peak) / peak) * 100 : 0;
    });
    return {
      animation: false,
      grid: { top: 8, bottom: 28, left: 56, right: 12 },
      xAxis: { type: "category", data: dates, axisLabel: { fontSize: 10, color: "#6b7280" }, boundaryGap: false },
      yAxis: {
        type: "value",
        axisLabel: { fontSize: 10, formatter: (v) => `${v.toFixed(0)}%`, color: "#6b7280" },
        splitLine: { lineStyle: { type: "dashed", color: "#f3f4f6" } },
      },
      series: [{
        type: "line",
        data: drawdownPct,
        areaStyle: { color: "rgba(239,68,68,0.15)" },
        lineStyle: { color: "#ef4444", width: 1.5 },
        symbol: "none",
        smooth: 0.3,
      }],
      tooltip: {
        trigger: "axis",
        formatter: (params) => `${params[0].axisValue}<br/>回撤 ${Number(params[0].value).toFixed(2)}%`,
      },
    };
  }, [navSeries, chartRange]);
  if (!option) return <div className="chart-empty">净值序列不足，无法计算回撤。</div>;
  return <div className="drawdown-chart-wrap"><Chart option={option} /></div>;
}

function niceAxisMin(values) {
  if (!values.length) return 0;
  const min = Math.min(...values);
  if (min > 0.75 && min < 1) return 0.8;
  if (min >= 1) return 1;
  if (min >= 0) return Math.floor(min * 20) / 20;
  return Math.floor(min);
}

function niceAxisMax(values) {
  if (!values.length) return 1;
  const max = Math.max(...values);
  if (max <= 2) return Math.ceil(max * 10) / 10;
  if (max <= 10) return Math.ceil(max * 2) / 2;
  return Math.ceil(max);
}

function nicePercentAxisMin(values) {
  if (!values.length) return 0;
  const min = Math.min(...values);
  if (min >= 0) return 0;
  return Math.floor((min * 100) / 10) * 10 / 100;
}

function nicePercentAxisMax(values) {
  if (!values.length) return 0.1;
  const max = Math.max(...values);
  if (max <= 0) return 0;
  return Math.ceil((max * 100) / 10) * 10 / 100;
}

function chartPercent(value) {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return "--";
  return `${(numberValue * 100).toLocaleString("zh-CN", { maximumFractionDigits: 2 })}%`;
}


export function AssetDonut({ holdings }) {
  const data = React.useMemo(
    () => {
      if (!holdings.length) {
        return [
          { name: "股票", value: 66.21 },
          { name: "ETF", value: 21.45 },
          { name: "债券", value: 9.71 },
          { name: "现金", value: 0.97 },
        ];
      }
      const grouped = new Map();
      for (const item of holdings) {
        const name = displayText(item.asset_type ?? item.asset_code ?? "其他");
        grouped.set(name, (grouped.get(name) ?? 0) + Number(item.market_value ?? 0));
      }
      return Array.from(grouped, ([name, value]) => ({ name, value }));
    },
    [holdings],
  );
  const option = React.useMemo(
    () => ({
      tooltip: { trigger: "item" },
      legend: {
        orient: "vertical",
        right: 0,
        top: "middle",
        itemWidth: 10,
        itemHeight: 10,
        textStyle: { color: "#53606f" },
      },
      series: [{
        type: "pie",
        radius: ["44%", "70%"],
        center: ["36%", "50%"],
        label: { show: false },
        labelLine: { show: false },
        data,
      }],
    }),
    [data],
  );
  return (
    <section className="chart-card donut-card">
      <h2>资产分布</h2>
      <Chart option={option} />
    </section>
  );
}

export function WaterfallChart({ items }) {
  const option = React.useMemo(() => {
    const safeItems = (items ?? []).map((item) => ({
      ...item,
      value: Number(item.value ?? 0),
    }));
    const values = safeItems.map((item) => Number(item.value ?? 0)).filter(Number.isFinite);
    const yAxisRange = contributionAxisRange(values);
    return {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        valueFormatter: chartNumber,
      },
      grid: { left: 58, right: 24, top: 36, bottom: 42 },
      xAxis: { type: "category", data: safeItems.map((item) => item.label), axisLabel: { interval: 0 } },
      yAxis: {
        type: "value",
        min: yAxisRange.min,
        max: yAxisRange.max,
        axisLabel: { formatter: (value) => chartAxisNumber(value) },
      },
      series: [
        {
          name: "金额",
          type: "bar",
          barMaxWidth: 72,
          data: safeItems.map((item) => ({
            value: item.value,
            itemStyle: { color: attributionColor(item) },
            label: {
              show: true,
              position: item.value > 0 ? "top" : item.value < 0 ? "bottom" : "top",
              formatter: () => chartAxisNumber(item.value),
              color: "#344054",
              fontSize: 12,
              fontWeight: 700,
            },
          })),
        },
      ],
    };
  }, [items]);
  return <Chart option={option} />;
}

function formatKlineQuantity(value) {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return "--";
  return numberValue.toLocaleString("zh-CN", { maximumFractionDigits: 4 });
}

function companyEventLabel(type) {
  return {
    cash_dividend: "分红",
    cash_dividend_entitlement: "分红权益",
    share_bonus: "送转",
    rights_issue: "配股",
    rights_issue_confirmation: "配股确认",
  }[type] ?? "公司事件";
}

function companyEventMarkerLabel(events) {
  const shortLabels = [...new Set(events.map((event) => ({
    cash_dividend: "分",
    cash_dividend_entitlement: "分",
    share_bonus: "送",
    rights_issue: "配",
    rights_issue_confirmation: "配",
  }[event.event_type] ?? "事")))];
  return shortLabels.join("/");
}

function companyEventMarkerColor(events) {
  const types = new Set(events.map((event) => event.event_type));
  if (types.size > 1) return "#7c3aed";
  if (types.has("cash_dividend") || types.has("cash_dividend_entitlement")) return "#2563eb";
  if (types.has("share_bonus")) return "#7c3aed";
  return "#c77914";
}

function eventImpactLabel(event) {
  const cash = Number(event.cash_delta ?? 0);
  const receivable = Number(event.receivable_delta ?? 0);
  const quantity = Number(event.quantity_delta ?? 0);
  const parts = [];
  if (Number.isFinite(cash) && cash !== 0) parts.push(`现金 ${cash > 0 ? "+" : "-"}${formatKlineMoney(Math.abs(cash))} 元`);
  if (Number.isFinite(receivable) && receivable !== 0) parts.push(`应收分红 ${receivable > 0 ? "+" : "-"}${formatKlineMoney(Math.abs(receivable))} 元`);
  if (Number.isFinite(quantity) && quantity !== 0) parts.push(`股份 ${quantity > 0 ? "+" : "-"}${formatKlineQuantity(Math.abs(quantity))} 股`);
  return parts.join("；") || "不直接改变现金或股份";
}

function formatKlineMoney(value) {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return "--";
  return numberValue.toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatKlineTooltip(
  params,
  visiblePrices,
  useNavLine,
  costLineName,
  comparisonNames,
) {
  const points = Array.isArray(params) ? params : [];
  const candle = points.find((point) => point.seriesType === "candlestick");
  const navLine = points.find((point) => point.seriesName === "NAV");
  const costLine = points.find((point) => point.seriesName === costLineName);
  const comparisons = points.filter((point) => comparisonNames.has(point.seriesName));
  const line = navLine ?? costLine;
  const date = candle?.axisValue ?? line?.axisValue ?? "--";
  const pricePoint = visiblePrices.find((point) => (point.date ?? point.trade_date) === date);
  const costLineValue = Number(costLine?.value);
  const costRow = Number.isFinite(costLineValue)
    ? `<br/>${costLine?.seriesName ?? "成本价"}：${formatKlineMoney(costLineValue)}`
    : "";
  const comparisonRows = comparisons.map((point) => (
    Number.isFinite(Number(point.value))
      ? `<br/>${point.seriesName}：${chartPercent(Number(point.value))}`
      : ""
  )).join("");
  if (useNavLine) {
    return `<strong>${date}</strong><br/>净值：${formatKlineMoney(pricePoint?.close ?? navLine?.value)}${costRow}${comparisonRows}`;
  }
  return [
    `<strong>${date}</strong>`,
    `开盘：${formatKlineMoney(pricePoint?.open)}`,
    `收盘：${formatKlineMoney(pricePoint?.close)}`,
    `最高：${formatKlineMoney(pricePoint?.high)}`,
    `最低：${formatKlineMoney(pricePoint?.low)}`,
  ].join("<br/>") + costRow + comparisonRows;
}

function contributionAxisRange(values) {
  const finiteValues = values.filter(Number.isFinite);
  if (!finiteValues.length) return { min: 0, max: 1 };
  const minValue = Math.min(...finiteValues, 0);
  const maxValue = Math.max(...finiteValues, 0);
  if (minValue === 0 && maxValue === 0) return { min: -1, max: 1 };
  const span = maxValue - minValue || Math.max(Math.abs(minValue), Math.abs(maxValue), 1);
  const padding = span * 0.12;
  return {
    min: Math.floor((minValue - padding) / 100) * 100,
    max: Math.ceil((maxValue + padding) / 100) * 100,
  };
}

export function BarChart({ items, unit = "", layout = "vertical" }) {
  const option = React.useMemo(() => {
    const horizontal = layout === "horizontal";
    const values = (items ?? []).map((item) => Number(item.value ?? 0));
    const labels = (items ?? []).map((item) => item.label);
    const valueAxis = {
      type: "value",
      axisLabel: { formatter: (value) => `${chartAxisNumber(value)}${unit}` },
    };
    const categoryAxis = {
      type: "category",
      data: labels,
      axisLabel: horizontal
        ? { width: 132, overflow: "truncate" }
        : { interval: 0 },
    };
    return {
      tooltip: { trigger: "axis", axisPointer: { type: "shadow" }, valueFormatter: chartNumber },
      grid: horizontal
        ? { left: 146, right: 94, top: 8, bottom: 8 }
        : { left: 54, right: 24, top: 28, bottom: 42 },
      xAxis: horizontal ? valueAxis : categoryAxis,
      yAxis: horizontal ? categoryAxis : valueAxis,
      series: [{
        type: "bar",
        data: values,
        label: {
          show: true,
          position: horizontal ? "right" : "top",
          formatter: (params) => chartNumber(params.value),
        },
        itemStyle: { color: (params) => Number(params.value) < 0 ? "#ef4444" : "#007a3d" },
      }],
    };
  }, [items, layout, unit]);
  return <Chart option={option} />;
}

function formatPercentTooltip(params, navSeries) {
  const items = Array.isArray(params) ? params : [];
  const first = items[0];
  if (!first) return "";
  const point = navSeries[first.dataIndex] ?? {};
  const lines = [`<strong>${first.axisValue ?? "--"}</strong>`];
  for (const item of items) {
    lines.push(`${item.marker ?? ""}${item.seriesName}: ${chartPercent(item.value)}`);
  }
  if (Number.isFinite(Number(point.active_member_count))) {
    lines.push(`纳入成员：${Number(point.active_member_count)} 个`);
  }
  return lines.join("<br/>");
}


export function Chart({ option, onChartClick = null }) {
  const ref = React.useRef(null);
  const chartRef = React.useRef(null);
  React.useEffect(() => {
    if (!ref.current) return undefined;
    const existing = echarts.getInstanceByDom(ref.current);
    const chart = existing ?? echarts.init(ref.current);
    chartRef.current = chart;
    const resize = () => chart.resize();
    window.addEventListener("resize", resize);
    return () => {
      window.removeEventListener("resize", resize);
      chart.dispose();
      chartRef.current = null;
    };
  }, []);
  React.useEffect(() => {
    // ponytail: animation off — 500+ points cause blank canvas during 1s default animation
    chartRef.current?.setOption({ ...option, animation: false }, { notMerge: true });
  }, [option]);
  React.useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !onChartClick) return undefined;
    chart.on("click", onChartClick);
    return () => chart.off("click", onChartClick);
  }, [onChartClick]);
  return <div className="echart" ref={ref} />;
}
